import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Instagram, Youtube, Globe } from "lucide-react";
import { companyInfo } from "@/data/products";
import pureproLogo from "@/assets/purepro-logo.png";
import { useSocialLinks } from "@/hooks/use-social-links";

const iconMap: Record<string, React.FC<{ className?: string }>> = {
  facebook: Facebook,
  instagram: Instagram,
  youtube: Youtube,
};

const Footer = () => {
  const { links: socialLinks } = useSocialLinks();

  const activeSocials = Object.entries(socialLinks).filter(([, url]) => url?.trim());

  return (
    <footer className="bg-primary text-primary-foreground">
      {/* Main Footer */}
      <div className="container-premium section-padding pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <img 
                src={pureproLogo} 
                alt="Pure Pro India Logo" 
                className="w-12 h-12 object-contain bg-white rounded-lg p-1"
              />
              <div>
                <h3 className="text-lg font-serif font-bold">{companyInfo.name}</h3>
                <p className="text-xs text-primary-foreground/70">Premium Water Systems</p>
              </div>
            </div>
            <p className="text-sm text-primary-foreground/80 leading-relaxed mb-6">
              {companyInfo.tagline}. {companyInfo.madeIn} - recognized worldwide for high quality products.
            </p>
            {activeSocials.length > 0 && (
              <div className="flex gap-4">
                {activeSocials.map(([name, url]) => {
                  const Icon = iconMap[name] || Globe;
                  return (
                    <a key={name} href={url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-primary-foreground/10 hover:bg-accent hover:text-primary flex items-center justify-center transition-all duration-300">
                      <Icon className="w-5 h-5" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

        {/* Quick Links */}
          <div>
            <h4 className="text-lg font-serif font-semibold mb-6 text-accent">Quick Links</h4>
            <ul className="space-y-3">
            {[
                { label: "Home", href: "/" },
                { label: "All Products", href: "/products" },
                { label: "Water Ionizers", href: "/products?category=ionizers" },
                { label: "RO Systems", href: "/products?category=ro-systems" },
                { label: "Hydrogen Products", href: "/products?category=hydrogen" },
                { label: "Perfect Water", href: "/products?category=perfect-water" },
                { label: "Health Benefits", href: "/health-benefits" },
                { label: "Blog", href: "/blog" },
                { label: "About Us", href: "/about-us" },
                { label: "Contact Us", href: "/contact-us" },
              ].map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm text-primary-foreground/80 hover:text-accent transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div>
            <h4 className="text-lg font-serif font-semibold mb-6 text-accent">Products</h4>
            <ul className="space-y-3">
              {companyInfo.productLines.map((item) => (
                <li key={item.name}>
                  <Link
                    to={`/products?category=${item.category}`}
                    className="text-sm text-primary-foreground/80 hover:text-accent transition-colors"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-serif font-semibold mb-6 text-accent">Contact Us</h4>
            <ul className="space-y-4">
              <li>
                <a href={`tel:${companyInfo.contact.phone}`} className="flex items-start gap-3 text-sm text-primary-foreground/80 hover:text-accent transition-colors">
                  <Phone className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span>{companyInfo.contact.phone}</span>
                </a>
              </li>
              <li>
                <a href={`mailto:${companyInfo.contact.email}`} className="flex items-start gap-3 text-sm text-primary-foreground/80 hover:text-accent transition-colors">
                  <Mail className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span>{companyInfo.contact.email}</span>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-sm text-primary-foreground/80">
                  <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span>{companyInfo.contact.headOffice.address}</span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-primary-foreground/10">
        <div className="container-premium py-6">
          <div className="text-center text-sm text-primary-foreground/60">
            <p>© 2025 {companyInfo.name}. All rights reserved.</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
